/**
 * Influencer Landing Page — /{slug}
 * ---------------------------------------------------------------------------
 * Conversion-focused landing page for influencer referral traffic.
 * Visitors arrive from the influencer's social channels already trusting
 * the creator — the page converts that trust into a client lead.
 *
 * Sections:
 *   1. Hero (ivory) — influencer identity + client registration form
 *   2. Benefit Partners (teal) — VIP hotel program logos
 *   3. How It Works (teal) — 4-step process
 *   4. Why LuxRally (ivory) — 6 value proposition cards
 *   5. Cinema Band — full-bleed video interlude
 *   6. Closing CTA (teal) — scroll-to-form
 *   7. Mobile Sticky Bottom Bar
 *
 * Single-file component (mirrors agents/show.tsx pattern).
 */

import { CollabMark } from '@/components/brand/CollabMark';
import { TaglineBadge } from '@/components/brand/TaglineBadge';
import { InquiryForm } from '@/components/inquiry/InquiryForm';
import MetaHead from '@/components/meta-head';
import ExclusiveAccessGridBlock from '@/components/page-builder/blocks/ExclusiveAccessGridBlock';
import HowItWorksBlock from '@/components/page-builder/blocks/HowItWorksBlock';
import ValuePropositionBlock from '@/components/page-builder/blocks/ValuePropositionBlock';
import PublicLayout from '@/layouts/public-layout';
import { cn } from '@/lib/utils';
import { motion, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface InfluencerContent {
    id: number;
    name: string;
    slug: string;
    bio: string | null;
    specialty: string | null;
    main_image: string | null;
    instagram_handle: string | null;
    youtube_channel: string | null;
    twitter_handle: string | null;
    tiktok_handle: string | null;
    facebook_url: string | null;
    linkedin_url: string | null;
    reddit_handle: string | null;
    website_url: string | null;
    followers_count: number;
    engagement_rate: string;
}

interface InfluencerLandingProps {
    page: unknown;
    content: unknown;
    metadata: unknown;
    dataContext: {
        influencer?: InfluencerContent;
        [key: string]: unknown;
    } | null;
}

/* ------------------------------------------------------------------ */
/*  Animation Tokens                                                   */
/* ------------------------------------------------------------------ */

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } },
};

const CTA_BTN =
    'lux-caps lux-caps-sm rounded-sm border border-white/30 bg-black/15 text-white backdrop-blur-sm transition-colors duration-300 hover:border-white/50 hover:bg-black/25';

/* ------------------------------------------------------------------ */
/*  Section content — mirrored from homepage page-builder blocks       */
/*  (page id=12, blocks 364, 350, 354) so /travelspree and the         */
/*  homepage stay in sync visually + editorially.                      */
/* ------------------------------------------------------------------ */

const EXCLUSIVE_ACCESS_CONTENT = {
    kicker: 'Exclusive Access',
    heading: 'Global Connections',
};

const HOW_IT_WORKS_CONTENT = {
    kicker: 'HOW IT WORKS',
    heading: 'From Vision to Departure',
    items: [
        {
            stepNumber: '1',
            title: 'Register',
            iconName: 'ClipboardList',
            description:
                'Complete a brief registration and share your plans. Your dedicated advisor reaches out personally to align on vision and priorities.',
        },
        {
            stepNumber: '2',
            title: 'Review',
            iconName: 'Zap',
            description:
                "Your advisor will talk through options and send a quote. Our tech gives us pricing visibility into non-public rates meaning you won't overpay.",
        },
        {
            stepNumber: '3',
            title: 'Authorize',
            iconName: 'ShieldCheck',
            description:
                'Confirm your selection and submit a secure authorization form. Your profile is stored, allowing future bookings to be handled with a simple text.',
        },
        {
            stepNumber: '4',
            title: 'Relax',
            iconName: 'Luggage',
            description:
                'Your advisor remains directly available by call or text, managing changes and handling whatever arises along the way.',
        },
    ],
};

// showCta=false matches the homepage — the influencer page already has the
// hero form above and the closing CTA below, so an extra CTA in this band
// would be noise.
const HOW_IT_WORKS_SETTINGS = {
    showCta: false,
    background: 'ivory' as const,
    showHeader: true,
};

const VALUE_PROPOSITION_CONTENT = {
    kicker: 'WHY LUXRALLY?',
    heading: 'We Know What You Want',
    closingLine: 'Your next trip, elevated.',
    closingSub: 'No fees. No markup. Just a better way to travel.',
    items: [
        {
            kicker: 'Better Rates',
            headline: 'The Rates You Never See',
            body: 'Hotels distribute thousands of unpublished rates through private channels — wholesalers, consortia, agency networks. We scan hundreds of these sources in seconds so you’re never paying more than you should.',
        },
        {
            kicker: 'Recognition',
            headline: 'They Know You’re Coming',
            body: 'When you book through us, your preferences arrive before you do. The room, the pillow, the wine you like — all communicated in advance. You walk in as a known guest, not a stranger checking in.',
        },
        {
            kicker: 'Advocacy',
            headline: 'Someone on Your Side',
            body: 'You reach your advisor directly. They speak to hotel management — not the front desk. No hold music. No repeating yourself. Actual representation when something goes wrong.',
        },
        {
            kicker: 'Zero Fees',
            headline: 'So What’s the Catch',
            body: 'There isn’t one. The hotel pays us — not you. You pay the same rate, often less, and get better treatment. It doesn’t cost you a thing.',
        },
        {
            kicker: 'Convenience',
            headline: 'It Only Gets Better',
            body: 'Each booking becomes faster than the last. Call or text your advisor with a destination — they’ll come back with options before you’ve finished thinking about it.',
        },
        {
            kicker: 'Personalization',
            headline: 'A Permanent Advantage',
            body: 'Your preferences, loyalty numbers, and travel history are stored securely. Future bookings take a single text. A relationship that compounds every time you travel.',
        },
    ],
};


/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function InfluencerLanding({ dataContext }: InfluencerLandingProps) {
    const shouldReduceMotion = useReducedMotion();
    const content = dataContext?.influencer;

    const [pageUrl, setPageUrl] = useState('');
    useEffect(() => {
        setPageUrl(window.location.href);
    }, []);

    /* ---- Derived ---- */
    const hasSocials =
        content?.instagram_handle ||
        content?.youtube_channel ||
        content?.twitter_handle ||
        content?.tiktok_handle ||
        content?.facebook_url ||
        content?.linkedin_url ||
        content?.reddit_handle ||
        content?.website_url;

    // Pluralize the kicker for duos ("Fain and Laura" → CREATORS).
    // \b ensures we don't false-match names like "Andrew" or "Andre".
    const isDuo = !!content?.name && /\b(and|&)\b/i.test(content.name);
    const kickerSubject = isDuo ? 'CREATORS' : 'CREATOR';

    /* ---- Handlers ---- */
    const scrollToForm = useCallback(() => {
        document.getElementById('influencer-hero')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    /* Listen for header CTA custom event */
    useEffect(() => {
        const handler = () => scrollToForm();
        window.addEventListener('influencer:scroll-to-form', handler);
        return () => window.removeEventListener('influencer:scroll-to-form', handler);
    }, [scrollToForm]);

    /* ---- Motion helper — no-op when reduced motion preferred ---- */
    const mp = (props: Record<string, unknown>) => (shouldReduceMotion ? {} : props);

    /* Guard: if no influencer data, render nothing (server should 404) */
    if (!content) return null;

    return (
        <>
            <PublicLayout
                headerConfig={{
                    position: 'fixed',
                    scrollBehavior: 'always',
                    showMenu: false,
                    showGetStarted: false,
                    showSearch: false,
                    showPhone: false,
                    logoAlignment: 'left',
                    showCta: true,
                    ctaText: 'Become a Client',
                    ctaTextMobile: 'Book',
                    ctaAction: 'custom',
                    ctaCustomEventName: 'influencer:scroll-to-form',
                    ctaStyle: 'filled',
                    tagline: '',
                }}
            >
                <MetaHead
                    title={`${content.name} x LuxRally Travel — Luxury Travel, Elevated`}
                    description={
                        content.bio
                            ? `${content.name} partners with LuxRally Travel. ${content.bio.slice(0, 120)}...`
                            : `${content.name} partners with LuxRally Travel for VIP luxury travel experiences.`
                    }
                />

                {/* ============================================= */}
                {/* HERO — Influencer identity + form              */}
                {/* ============================================= */}
                <section id="influencer-hero" className="w-full bg-[#0b2222]">
                    <div className="mx-auto max-w-[1600px] px-6 pt-[36px] pb-8 md:px-12 md:pt-[48px] md:pb-10 lg:px-20 lg:pt-[60px] lg:pb-12 xl:px-24">
                        {/* 3-column grid mirrors agent-page hero: type column,
                            1px vertical divider, form column. Single teal field
                            (no separate panel backgrounds), which lets the divider
                            seam read cleanly through the spread. */}
                        <div className="grid grid-cols-1 items-start gap-16 lg:grid-cols-[minmax(0,1fr)_1px_clamp(440px,36vw,600px)] lg:gap-0">
                        {/* Left column — photo card on top, typographic
                            stack below. Both share the same width and sit
                            centered in the column so the editorial composition
                            reads as a single anchored block (text inside stays
                            left-aligned for legibility). */}
                        <motion.div
                            {...mp({
                                initial: { opacity: 0 },
                                animate: { opacity: 1, transition: { duration: 1, ease: EASE } },
                            })}
                            className="mx-auto flex w-full max-w-[560px] flex-col lg:-mt-[15px]"
                        >
                            <motion.div
                                {...mp({
                                    variants: stagger,
                                    initial: 'hidden',
                                    animate: 'visible',
                                })}
                            >
                                    {/* Kicker — TaglineBadge wordmark on either side of
                                        the CollabMark double-hairline × glyph. Keeps the
                                        original SVG-rendered wordmark format; only the
                                        × character is swapped for the brand mark. */}
                                    <motion.div
                                        {...mp({ variants: fadeUp })}
                                        className="inline-flex items-center gap-1.5 text-[#F5F5F5]/50"
                                    >
                                        <TaglineBadge text="LUXRALLY" className="h-[7px] w-auto" />
                                        <CollabMark size={11} />
                                        <TaglineBadge text={kickerSubject} className="h-[7px] w-auto" />
                                    </motion.div>

                                    {/* Name — clean Didot on solid teal. Sized so a
                                        typical 2-3 word name fits the type column
                                        on one line without dominating the photo card. */}
                                    <motion.h1
                                        {...mp({ variants: fadeUp })}
                                        className="lux-didot mt-3 text-[#F5F5F5]"
                                        style={{ fontSize: 'clamp(2.75rem, 5.5vw, 5rem)', lineHeight: 0.95 }}
                                    >
                                        {content.name}
                                    </motion.h1>


                                    {/* Social row — inline glyph + handle treatment.
                                        URLs strip leading @ / u/ from stored handles since
                                        platform URLs don't include the prefix. */}
                                    {hasSocials && (
                                        <motion.div {...mp({ variants: fadeUp })} className="mt-1 flex flex-wrap gap-x-5 gap-y-2">
                                            {content.instagram_handle && (
                                                <a
                                                    href={`https://instagram.com/${content.instagram_handle.replace(/^@/, '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label="Instagram"
                                                    className="group inline-flex items-center gap-2 text-[#F5F5F5]/65 transition-colors hover:text-[#F5F5F5]"
                                                >
                                                    <svg className="h-[15px] w-[15px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                                                    </svg>
                                                
                                                    <span className="lux-caps lux-caps-sm" style={{ letterSpacing: '0.18em' }}>
                                                        {content.instagram_handle.startsWith('@') ? content.instagram_handle : `@${content.instagram_handle}`}
                                                    </span>
                                                </a>
                                            )}
                                            {content.youtube_channel && (
                                                <a
                                                    href={`https://youtube.com/${content.youtube_channel.startsWith('@') ? content.youtube_channel : `@${content.youtube_channel}`}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label="YouTube"
                                                    className="group inline-flex items-center gap-2 text-[#F5F5F5]/65 transition-colors hover:text-[#F5F5F5]"
                                                >
                                                    <svg className="h-[15px] w-[15px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                                    </svg>
                                                
                                                    <span className="lux-caps lux-caps-sm" style={{ letterSpacing: '0.18em' }}>
                                                        {content.youtube_channel.startsWith('@') ? content.youtube_channel : `@${content.youtube_channel}`}
                                                    </span>
                                                </a>
                                            )}
                                            {content.tiktok_handle && (
                                                <a
                                                    href={`https://tiktok.com/@${content.tiktok_handle.replace(/^@/, '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label="TikTok"
                                                    className="group inline-flex items-center gap-2 text-[#F5F5F5]/65 transition-colors hover:text-[#F5F5F5]"
                                                >
                                                    <svg className="h-[15px] w-[15px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                                                    </svg>
                                                
                                                    <span className="lux-caps lux-caps-sm" style={{ letterSpacing: '0.18em' }}>
                                                        {content.tiktok_handle.startsWith('@') ? content.tiktok_handle : `@${content.tiktok_handle}`}
                                                    </span>
                                                </a>
                                            )}
                                            {content.twitter_handle && (
                                                <a
                                                    href={`https://x.com/${content.twitter_handle.replace(/^@/, '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label="X (Twitter)"
                                                    className="group inline-flex items-center gap-2 text-[#F5F5F5]/65 transition-colors hover:text-[#F5F5F5]"
                                                >
                                                    <svg className="h-[15px] w-[15px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                                    </svg>
                                                
                                                    <span className="lux-caps lux-caps-sm" style={{ letterSpacing: '0.18em' }}>
                                                        {content.twitter_handle.startsWith('@') ? content.twitter_handle : `@${content.twitter_handle}`}
                                                    </span>
                                                </a>
                                            )}
                                            {content.facebook_url && (
                                                <a
                                                    href={content.facebook_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label="Facebook"
                                                    className="group inline-flex items-center gap-2 text-[#F5F5F5]/65 transition-colors hover:text-[#F5F5F5]"
                                                >
                                                    <svg className="h-[15px] w-[15px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                                    </svg>
                                                
                                                    <span className="lux-caps lux-caps-sm" style={{ letterSpacing: '0.18em' }}>
                                                        {'Facebook'}
                                                    </span>
                                                </a>
                                            )}
                                            {content.linkedin_url && (
                                                <a
                                                    href={content.linkedin_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label="LinkedIn"
                                                    className="group inline-flex items-center gap-2 text-[#F5F5F5]/65 transition-colors hover:text-[#F5F5F5]"
                                                >
                                                    <svg className="h-[15px] w-[15px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                                    </svg>
                                                
                                                    <span className="lux-caps lux-caps-sm" style={{ letterSpacing: '0.18em' }}>
                                                        {'LinkedIn'}
                                                    </span>
                                                </a>
                                            )}
                                            {content.reddit_handle && (
                                                <a
                                                    href={`https://reddit.com/user/${content.reddit_handle.replace(/^u\//, '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label="Reddit"
                                                    className="group inline-flex items-center gap-2 text-[#F5F5F5]/65 transition-colors hover:text-[#F5F5F5]"
                                                >
                                                    <svg className="h-[15px] w-[15px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path d="M12 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 01-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 01.042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 014.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 01.14-.197.35.35 0 01.238-.042l2.906.617a1.214 1.214 0 011.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 00-.231.094.33.33 0 000 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 00.029-.463.33.33 0 00-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 00-.232-.095z" />
                                                    </svg>
                                                
                                                    <span className="lux-caps lux-caps-sm" style={{ letterSpacing: '0.18em' }}>
                                                        {`u/${content.reddit_handle.replace(/^u\//, '')}`}
                                                    </span>
                                                </a>
                                            )}
                                            {content.website_url && (
                                                <a
                                                    href={content.website_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label="Website"
                                                    className="group inline-flex items-center gap-2 text-[#F5F5F5]/65 transition-colors hover:text-[#F5F5F5]"
                                                >
                                                    <svg
                                                        className="h-[15px] w-[15px]"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        viewBox="0 0 24 24"
                                                        aria-hidden="true"
                                                    >
                                                        <circle cx="12" cy="12" r="10" />
                                                        <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                                                    </svg>
                                                
                                                    <span className="lux-caps lux-caps-sm" style={{ letterSpacing: '0.18em' }}>
                                                        {'Website'}
                                                    </span>
                                                </a>
                                            )}
                                        </motion.div>
                                    )}

                                    {/* Landscape photo card — fills the column
                                        width, sits between the masthead (kicker
                                        + name) above and the editorial body
                                        (paragraph + CTA + social) below. */}
                                    {content.main_image && (
                                        <motion.div {...mp({ variants: fadeUp })} className="relative mt-5 aspect-[16/9] w-full overflow-hidden rounded-md">
                                            <img
                                                src={content.main_image}
                                                alt={content.name}
                                                className="h-full w-full object-cover object-[center_25%]"
                                                style={shouldReduceMotion ? undefined : { animation: 'expandingHeroBreath 20s ease-in-out infinite' }}
                                                loading="eager"
                                            />
                                        </motion.div>
                                    )}

                                    {/* Partner intro paragraph — fills the centered
                                        column width so it visually aligns with the
                                        photo card edges. */}
                                    <motion.p
                                        {...mp({ variants: fadeUp })}
                                        className="lux-body lux-body-sm mt-8 leading-[1.85] text-[#F5F5F5]/80"
                                    >
                                        Our partners provide candid, first-hand insights so you know exactly what to expect before you arrive. From
                                        luxury hotels to world-class cruises, our partnership ensures their followers receive the best possible perks,
                                        preferred rates, and elevated service.
                                    </motion.p>

                                    {/* CTA — solid dark fill (#081c1c) per the homepage
                                        hero "Join Today" treatment. Used on solid teal
                                        backgrounds; the frosted-ghost variant is reserved
                                        for over-image/video contexts. */}
                                    <motion.div {...mp({ variants: fadeUp })} className="mt-8 flex items-center gap-4">
                                        <button
                                            type="button"
                                            onClick={scrollToForm}
                                            className="lux-caps lux-caps-sm cursor-pointer rounded-sm border border-[#F5F5F5]/30 bg-[#081c1c] px-4 py-1 text-[#F5F5F5] transition-colors duration-300 hover:border-[#F5F5F5]/50 hover:bg-[#0a2020] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5F5F5]/60"
                                        >
                                            Get Booked
                                        </button>
                                        <span className="lux-body text-[10px] text-[#F5F5F5]/55" style={{ letterSpacing: '0.02em' }}>
                                            Zero fees. No commitment.
                                        </span>
                                    </motion.div>
                            </motion.div>
                        </motion.div>

                        {/* Vertical divider — desktop only, between type column
                            and form column. Mirrors agent-page hero treatment. */}
                        <div className="hidden self-stretch bg-[#F5F5F5]/12 lg:block" aria-hidden="true" />

                        {/* Right column — shared InquiryForm. Sizes + spacing
                            match the agent-page form column for visual cohesion. */}
                        <motion.div
                            {...mp({
                                initial: { opacity: 0, y: 20 },
                                animate: { opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.2, ease: EASE } },
                            })}
                            className="sm:px-10 md:px-16 lg:self-center lg:px-0 lg:pl-10 xl:pl-14"
                        >
                            <header className="mb-8 text-center">
                                <h2 className="lux-didot text-[clamp(2.25rem,4vw,3.75rem)] leading-[0.95] text-[#F5F5F5] lg:text-[28px] xl:text-[32px]">
                                    Plan Your Next Journey
                                </h2>
                                <TaglineBadge
                                    text="NO FEES · PRIVATE RATES · BOOKED IN MINUTES"
                                    className="mx-auto mt-5 h-[7px] w-auto max-w-full text-[#F5F5F5]/50"
                                />
                            </header>
                            <InquiryForm
                                context={{
                                    source: 'influencer_page',
                                    influencer_id: content.id,
                                    influencer_name: content.name,
                                    page_url: pageUrl,
                                }}
                                hideHeader
                            />
                        </motion.div>
                        </div>
                    </div>
                </section>

                {/* Sections below the hero — same page-builder block components
                    used on the homepage, so the influencer page reads as the same
                    family of editorial sections. Content matches homepage page id=12
                    (blocks 364, 350, 354) so visual + copy stay synced if the
                    homepage evolves. */}
                <ExclusiveAccessGridBlock rawContent={EXCLUSIVE_ACCESS_CONTENT} />
                <HowItWorksBlock rawContent={HOW_IT_WORKS_CONTENT} settings={HOW_IT_WORKS_SETTINGS} />
                <ValuePropositionBlock rawContent={VALUE_PROPOSITION_CONTENT} />

                {/* ============================================= */}
                {/* CINEMA BAND — video interlude                  */}
                {/* ============================================= */}
                <section className="relative w-full overflow-hidden" style={{ height: 'clamp(50vh, 70vh, 80vh)' }}>
                    <video
                        className="absolute inset-0 h-full w-full object-cover"
                        style={{ animation: 'expandingHeroBreath 20s ease-in-out infinite' }}
                        loop
                        muted
                        playsInline
                        autoPlay
                        preload="metadata"
                        poster="https://cdnweb.luxrallytravel.com/2026/03/7fd6ac1a-cc22-411f-8e24-c20b4c712833.png"
                    >
                        <source
                            src="https://cdnweb.luxrallytravel.com/parity-migration/2025/08/de567095-10a8-4840-9a9b-42d6d5a16450.mp4"
                            type="video/mp4"
                        />
                    </video>

                    <div
                        className="absolute inset-x-0 bottom-0 z-[1] h-2/5"
                        style={{
                            background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.6) 40%, transparent 100%)',
                        }}
                    />

                    <motion.div
                        {...mp({
                            initial: { opacity: 0, y: 12 },
                            whileInView: { opacity: 1, y: 0 },
                            viewport: { once: true, amount: 0.3 },
                            transition: { duration: 0.6, ease: EASE },
                        })}
                        className="absolute inset-x-0 bottom-0 z-10"
                    >
                        <div className="flex items-center justify-between px-6 py-5 md:px-10 md:py-6">
                            <div className="flex flex-col gap-1">
                                <span className="lux-caps lux-caps-sm text-[#F5F5F5]/50">Forbes Travel Guide Endorsed Agency</span>
                                <h2 className="lux-didot lux-didot-lg text-[#F5F5F5]">For the Apex Traveler</h2>
                                <p className="lux-body lux-body-sm mt-0.5 text-[#F5F5F5]/60">Intelligence applied. Travel executed. No fees.</p>
                            </div>

                            <button
                                type="button"
                                onClick={scrollToForm}
                                className={cn(CTA_BTN, 'ml-8 hidden shrink-0 px-4 py-1.5 backdrop-blur-sm md:inline-flex')}
                            >
                                Start Planning
                            </button>
                        </div>

                        <div className="px-6 pb-5 md:hidden">
                            <button type="button" onClick={scrollToForm} className={cn(CTA_BTN, 'px-4 py-1.5 backdrop-blur-sm')}>
                                Start Planning
                            </button>
                        </div>
                    </motion.div>
                </section>

                {/* ============================================= */}
                {/* CLOSING CTA (teal)                             */}
                {/* ============================================= */}
                <motion.section
                    className="lux-space-lg nav-px w-full bg-[#0e2727]"
                    {...mp({
                        initial: 'hidden',
                        whileInView: 'visible',
                        viewport: { once: true, amount: 0.3 },
                        variants: fadeUp,
                    })}
                >
                    <div className="mx-auto max-w-[1600px]">
                        <div className="mx-auto max-w-2xl text-center">
                            {/* Partnership lockup — same pattern as the hero kicker:
                                TaglineBadge wordmarks on either side of the CollabMark
                                double-hairline × glyph. */}
                            <div className="mb-5 inline-flex items-center justify-center gap-1.5 text-[#F5F5F5]/50">
                                <TaglineBadge text={content.name.toUpperCase()} className="h-[7px] w-auto max-w-full" />
                                <CollabMark size={11} />
                                <TaglineBadge text="LUXRALLY TRAVEL" className="h-[7px] w-auto max-w-full" />
                            </div>
                            <h2 className="lux-didot lux-didot-xl text-[#F5F5F5]">
                                Your Next Trip,
                                <br />
                                Elevated
                            </h2>
                            <p className="lux-body lux-body-sm mx-auto mt-5 max-w-sm text-balance text-[#F5F5F5]/60">
                                Better rates, VIP recognition, and a dedicated advisor — at no cost to you.
                            </p>
                            <button
                                type="button"
                                onClick={scrollToForm}
                                className="lux-caps lux-caps-sm mt-10 inline-block rounded-sm border border-[#F5F5F5]/30 bg-[#081c1c] px-4 py-1.5 text-[#F5F5F5] transition-colors duration-300 hover:border-[#F5F5F5]/50 hover:bg-[#0a2020]"
                            >
                                Start Planning Your Trip
                            </button>
                        </div>
                    </div>
                </motion.section>

                {/* Bottom padding spacer for mobile sticky CTA */}
                <div className="h-20 lg:hidden" />
            </PublicLayout>

            {/* ============================================= */}
            {/* Portal: Mobile Sticky CTA                     */}
            {/* ============================================= */}
            {typeof document !== 'undefined' &&
                createPortal(
                    <div
                        className="fixed inset-x-0 bottom-0 z-40 lg:hidden"
                        style={{ backgroundColor: '#0e2727', paddingBottom: 'env(safe-area-inset-bottom)' }}
                    >
                        <div className="px-4 py-3">
                            <button
                                type="button"
                                onClick={scrollToForm}
                                className="lux-caps lux-caps-sm w-full rounded-sm border border-[#F5F5F5]/30 bg-[#081c1c] py-3 text-center text-[#F5F5F5] transition-colors duration-300 hover:border-[#F5F5F5]/50 hover:bg-[#0a2020]"
                            >
                                Start Planning Your Trip
                            </button>
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    );
}
